import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Switch, FormControlLabel, Divider } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import T from 'i18n';
import { ToggleButton } from 'features/common';
import api from 'common/api';
import { useSelector } from 'react-redux';
import { selectCurrentCaseId } from 'common/selectors';

const NewReindexContent = ({
  pipelineVersion: _pipelineVersion,
  setValuesHaveNotBeenSelected,
  show,
}) => {
  const { control, watch, setValue, getValues } = useFormContext();
  const caseId = useSelector(selectCurrentCaseId);
  const [stages, setStages] = useState([]);

  const stageKeys = useMemo(() => stages.map((stage) => stage.formKey), [stages]);
  const watchedValues = watch(stageKeys);
  const stageValues = Array.isArray(watchedValues) ? watchedValues : stageKeys.map(() => false);
  const firstCheckedIndex = stageValues.findIndex((value) => Boolean(value));
  const cascadeValue = watch('cascade');
  const isCascadeEnabled = cascadeValue !== false; // default true when undefined

  const handleToggle = (index, checked) => {
    if (!isCascadeEnabled) return;
    stages.forEach((_, idx) => {
      if (idx >= index) {
        setValue(stages[idx].formKey, checked, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    });
  };

  useEffect(() => {
    if (stageValues.some((value) => value)) {
      setValuesHaveNotBeenSelected(false);
    } else {
      setValuesHaveNotBeenSelected(true);
    }
  }, [stageValues, setValuesHaveNotBeenSelected]);

  useEffect(() => {
    if (!show) {
      return;
    }

    let isActive = true;

    const fetchPipelineStages = async () => {
      if (!caseId) {
        setStages(stageKeys);
        return;
      }

      try {
        const response = await api.get(`/cases/${caseId}/pipeline-stages`);
        if (!isActive) return;

        if (response?.error || !Array.isArray(response?.stages)) {
          setStages(stageKeys);
          return;
        }

        const normalizedStages = response.stages
          .filter((stage) => stage?.name)
          .sort((a, b) => {
            const seqA = typeof a.sequence === 'number' ? a.sequence : Number.MAX_SAFE_INTEGER;
            const seqB = typeof b.sequence === 'number' ? b.sequence : Number.MAX_SAFE_INTEGER;
            return seqA - seqB;
          })
          .map((stage) => ({
            formKey: stage.name,
            label: stage.displayName,
            enabled: stage.enabled !== false,
            sequence: typeof stage.sequence === 'number' ? stage.sequence : Number.MAX_SAFE_INTEGER,
          }));

        if (normalizedStages.length === 0) {
          setStages(stageKeys);
          return;
        }

        setStages(normalizedStages);
        normalizedStages.forEach((stage) => {
          const currentValue = getValues(stage.formKey);
          if (typeof currentValue === 'undefined') {
            setValue(stage.formKey, false, {
              shouldValidate: false,
              shouldDirty: false,
            });
          }
        });
      } catch (error) {
        if (!isActive) return;
        setStages(stageKeys);
      }
    };

    fetchPipelineStages();

    return () => {
      isActive = false;
    };
  }, [show, caseId, setValue, getValues]);

  return (
    <>
      <Grid container direction="column" spacing={1} padding={1}>
        {stages.map((stage, index) => (
          <Grid item xs={12} key={stage.formKey}>
            <Controller
              name={stage.formKey}
              control={control}
              defaultValue={false}
              render={({ field: { value, onChange, ref } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onChange(checked);
                        handleToggle(index, checked);
                      }}
                      name={stage.formKey}
                      inputRef={ref}
                      disabled={
                        stage.enabled === false ||
                        (isCascadeEnabled && firstCheckedIndex !== -1 && index > firstCheckedIndex)
                      }
                      as={ToggleButton}
                      sx={{ width: '5rem' }}
                    />
                  }
                  label={stage.label}
                  labelPlacement="start"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '90%',
                    margin: 0,
                  }}
                />
              )}
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="force"
            control={control}
            defaultValue={false}
            render={({ field: { value, onChange, ref } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onChange(checked);
                    }}
                    name="force"
                    inputRef={ref}
                    as={ToggleButton}
                    sx={{ width: '5rem' }}
                  />
                }
                label={T.translate('case.forceReindex') || 'Force Reindex'}
                labelPlacement="start"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '90%',
                  margin: 0,
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="cascade"
            control={control}
            defaultValue={true}
            render={({ field: { value, onChange, ref } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    name="cascade"
                    inputRef={ref}
                    as={ToggleButton}
                    sx={{ width: '5rem' }}
                  />
                }
                label={T.translate('case.cascade') || 'Cascade'}
                labelPlacement="start"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '90%',
                  margin: 0,
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default NewReindexContent;
